import React, { useState, useEffect, useCallback } from 'react';
import {
    TextField,
    makeStyles,
    CircularProgress,
    TableRow,
    TableCell,
    IconButton,
    Box,
} from '@material-ui/core';
import useDebounce from '@forvalter/utils/useDebounce';
import type { Company } from '../../services/rtkApi';
import { useLazyGetLeverandorvurderingQuery } from '../../services/rtkApi';
import { FormikNumericFormat } from '../FormikNumericFormat';
import { Checkbox } from '@klp/design/Checkbox';
import type { Leverandor } from '../LeverandorListSelector';
import { IconName, Illustration } from '@klp/design/Illustrations';
import { Typography } from '@klp/design';
import { useTranslation } from 'react-i18next';
import { CompanySearchField } from '../CompanySelector/CompanySearchField';
import classNames from 'classnames';

const useStyles = makeStyles((theme) => ({
    readOnlyField: {
        backgroundColor: theme.palette.primary.veryLight,
        '& .MuiOutlinedInput-notchedOutline': {
            borderColor: 'transparent', // Hides the border
        },
        '& .MuiOutlinedInput-input': {
            cursor: 'not-allowed',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
            fontSize: '1.5rem',
        },
    },
    editableField: {
        '& .MuiInputBase-root': {
            color: theme.palette.text.primary,
        },
        '& .Mui-disabled': {
            cursor: 'not-allowed',
        },
        '& .MuiFormLabel-asterisk': {
            color: theme.palette.error.main,
            fontSize: '1.5rem',
        },
    },
    alignCenter: {
        display: 'flex',
        justifyContent: 'center',
        paddingLeft: theme.spacing(1),
        paddingRight: theme.spacing(1),
    },
    labelRequired: {
        display: 'inline',
        color: theme.palette.error.main,
        fontSize: '1.5rem',
    },
    row: {
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            display: 'block',
            marginBottom: theme.spacing(2),
            border: '1px solid #ddd',
            borderRadius: theme.shape.borderRadius,
            padding: theme.spacing(2),
        },
    },
    cell: {
        [theme.breakpoints.down('sm')]: {
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'flex-start',
            padding: theme.spacing(1, 0),
            borderBottom: '1px solid #ddd',
            '&:last-child': {
                borderBottom: 'none',
            },
        },
    },
    cellContent: {
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        [theme.breakpoints.down('sm')]: {
            flexDirection: 'column',
            alignItems: 'flex-start',
        },
    },
    labelMobile: {
        display: 'none',
        [theme.breakpoints.down('sm')]: {
            display: 'block',
            fontWeight: theme.typography.fontWeightBold,
            marginBottom: theme.spacing(0.5),
        },
    },
}));

export const LeverandorListRow = ({
    leverandor,
    leverandorIndex,
    isReadOnly,
    isRequired,
    onChange,
    canDeleteRow,
    onDeleteRow,
}: {
    leverandor: Leverandor;
    leverandorIndex: number;
    isReadOnly: boolean;
    isRequired?: boolean;
    onChange: (value: Leverandor) => void;
    canDeleteRow: boolean;
    onDeleteRow: () => void;
}) => {
    const { t } = useTranslation();
    const classes = useStyles();
    const [formLeverandor, setFormLeverandor] =
        useState<Leverandor>(leverandor);

    // Local state for price fields to enable debouncing
    const [localPris, setLocalPris] = useState<number | undefined>(
        leverandor.pris,
    );
    const [localPrisKorrigert, setLocalPrisKorrigert] = useState<
        number | undefined
    >(leverandor.prisKorrigert);

    // Debounce the price updates
    const { debouncedValue: debouncedPris } = useDebounce(localPris, 300);
    const { debouncedValue: debouncedPrisKorrigert } = useDebounce(
        localPrisKorrigert,
        300,
    );

    const [selectedCompany, setSelectedCompany] = useState<Company | null>(
        leverandor.organisasjonsnummer
            ? ({
                  navn: leverandor.navn || leverandor.leverandorInfo || '',
                  organisasjonsnummer: leverandor.organisasjonsnummer || '',
                  konkurs: null,
              } satisfies Company)
            : null,
    );
    const [
        getLeverandorvurdering,
        {
            isFetching: leverandorvurderingIsFetching,
            isLoading: leverandorvurderingIsLoading,
            data: leverandorvurdering,
        },
    ] = useLazyGetLeverandorvurderingQuery();

    const handleOnChange = useCallback(
        (updatedLeverandor: Leverandor) => {
            setFormLeverandor(updatedLeverandor);
            onChange(updatedLeverandor);
        },
        [onChange],
    );

    // Sync local state when leverandor prop changes from parent
    useEffect(() => {
        setLocalPris(leverandor.pris);
        setLocalPrisKorrigert(leverandor.prisKorrigert);
        setFormLeverandor(leverandor);
    }, [leverandor]);

    // Handle debounced price updates
    useEffect(() => {
        if (debouncedPris !== formLeverandor.pris) {
            const updatedLeverandor: Leverandor = {
                ...formLeverandor,
                pris: debouncedPris,
            };
            handleOnChange(updatedLeverandor);
        }
    }, [debouncedPris, formLeverandor, handleOnChange]);

    useEffect(() => {
        if (debouncedPrisKorrigert !== formLeverandor.prisKorrigert) {
            const updatedLeverandor: Leverandor = {
                ...formLeverandor,
                prisKorrigert: debouncedPrisKorrigert,
            };
            handleOnChange(updatedLeverandor);
        }
    }, [debouncedPrisKorrigert, formLeverandor, handleOnChange]);

    useEffect(() => {
        if (selectedCompany && !isReadOnly) {
            getLeverandorvurdering({
                orgnummer: selectedCompany.organisasjonsnummer,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedCompany]);

    useEffect(() => {
        if (!leverandorvurdering) return;

        const leverandorNew: Leverandor = {
            ...formLeverandor,
            leverandorKlassifisering:
                leverandorvurdering?.klassifiseringBeskrivelse || '-',
        };
        handleOnChange(leverandorNew);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [leverandorvurdering]);

    const handleCompanyChange = (company: Company) => {
        const leverandorInfo = `${company.navn} (${company.organisasjonsnummer})`;

        const newLeverandor: Leverandor = {
            ...formLeverandor,
            organisasjonsnummer: company.organisasjonsnummer,
            navn: company.navn,
            leverandorInfo: leverandorInfo,
        };
        setSelectedCompany(company);
        handleOnChange(newLeverandor);
    };

    const handleCompanyClear = () => {
        const newLeverandor: Leverandor = {
            ...formLeverandor,
            organisasjonsnummer: undefined,
            navn: undefined,
            leverandorInfo: undefined,
        };
        setSelectedCompany(null);
        handleOnChange(newLeverandor);
    };

    return (
        <TableRow className={classes.row}>
            <TableCell className={classes.cell}>
                <Box className={classes.cellContent}>
                    <Typography
                        variant="caption"
                        className={classes.labelMobile}
                    >
                        {t('prosjekt.leverandor.organizationSelect')}
                        {isRequired && (
                            <Typography className={classes.labelRequired}>
                                &nbsp;*
                            </Typography>
                        )}
                    </Typography>

                    {isReadOnly ? (
                        <TextField
                            disabled={true}
                            fullWidth={true}
                            inputProps={{
                                'aria-readonly': true,
                            }}
                            className={classNames({
                                [classes.editableField]: !isReadOnly,
                                [classes.readOnlyField]: isReadOnly,
                            })}
                            id={`leverandor ${leverandor.organisasjonsnummer} organisasjonsnummer ${leverandorIndex}`}
                            name="organisasjonsnummer"
                            title={leverandor.leverandorInfo}
                            defaultValue={leverandor.leverandorInfo}
                            variant="outlined"
                        />
                    ) : (
                        <CompanySearchField
                            id={`leverandor-${leverandor.organisasjonsnummer}-organisasjonsnummer-${leverandorIndex}`}
                            label=""
                            placeholder={t(
                                'prosjekt.leverandor.organizationPlaceholder',
                            )}
                            isReadOnly={isReadOnly}
                            isRequired={isRequired}
                            defaultValue={leverandor.organisasjonsnummer}
                            selectedCompany={selectedCompany}
                            onChange={handleCompanyChange}
                            onClear={handleCompanyClear}
                        />
                    )}
                </Box>
            </TableCell>
            <TableCell className={classes.cell}>
                <Box className={classes.cellContent}>
                    <Typography
                        variant="caption"
                        className={classes.labelMobile}
                    >
                        {t('prosjekt.leverandor.price')}
                    </Typography>

                    <FormikNumericFormat
                        id={`leverandor ${leverandor.organisasjonsnummer} pris ${leverandorIndex}`}
                        variant="outlined"
                        fullWidth={true}
                        decimalScale={2}
                        isReadOnly={isReadOnly}
                        customInput={TextField}
                        value={localPris}
                        onChange={(event) => {
                            const newValue =
                                parseInt(event.target.value) || undefined;
                            setLocalPris(newValue);
                        }}
                    />
                </Box>
            </TableCell>
            <TableCell className={classes.cell}>
                <Box className={classes.cellContent}>
                    <Typography
                        variant="caption"
                        className={classes.labelMobile}
                    >
                        {t('prosjekt.leverandor.priceRevised')}
                    </Typography>
                    <FormikNumericFormat
                        id={`leverandor ${leverandor.organisasjonsnummer} prisKorrigert ${leverandorIndex}`}
                        variant="outlined"
                        fullWidth={true}
                        decimalScale={2}
                        isReadOnly={isReadOnly}
                        customInput={TextField}
                        value={localPrisKorrigert}
                        onChange={(event) => {
                            const newValue =
                                parseFloat(event.target.value) || undefined;
                            setLocalPrisKorrigert(newValue);
                        }}
                    />
                </Box>
            </TableCell>
            <TableCell className={classes.cell}>
                <Box className={classes.cellContent}>
                    <Typography
                        variant="caption"
                        className={classes.labelMobile}
                    >
                        {t('prosjekt.leverandor.klassifisering')}
                    </Typography>

                    <Box className={classes.alignCenter}>
                        {isReadOnly ? (
                            leverandor?.leverandorKlassifisering || '-'
                        ) : leverandorvurderingIsLoading ||
                          leverandorvurderingIsFetching ? (
                            <CircularProgress color="inherit" />
                        ) : (
                            leverandorvurdering?.klassifiseringBeskrivelse ||
                            '-'
                        )}
                    </Box>
                </Box>
            </TableCell>
            <TableCell className={classes.cell}>
                <Box className={classes.cellContent}>
                    <Typography
                        variant="caption"
                        className={classes.labelMobile}
                    >
                        {t('prosjekt.leverandor.selected')}
                    </Typography>
                    <Checkbox
                        id={`leverandor ${leverandor.organisasjonsnummer} valgtLeverandor ${leverandorIndex}`}
                        defaultChecked={formLeverandor.valgtLeverandor}
                        disabled={isReadOnly}
                        aria-disabled={isReadOnly}
                        onChange={(event) => {
                            const updatedLeverandor: Leverandor = {
                                ...formLeverandor,
                                valgtLeverandor: event.target.checked,
                            };
                            handleOnChange(updatedLeverandor);
                        }}
                    />
                </Box>
            </TableCell>
            <TableCell className={classes.cell}>
                <Box className={classes.cellContent}>
                    {canDeleteRow && !isReadOnly && (
                        <IconButton
                            onClick={onDeleteRow}
                            color="primary"
                            data-testid="delete-leverandor"
                        >
                            <Illustration
                                icon={IconName.DELETE}
                                klpPalette="primary"
                                klpColor="main"
                            />
                        </IconButton>
                    )}
                </Box>
            </TableCell>
        </TableRow>
    );
};
